import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import AISuggestReplies from '../components/social/AISuggestReplies';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  MessageSquare,
  Send,
  Search,
  Check,
  CheckCheck,
  Mail,
  Instagram,
  MessageCircle,
  Phone,
  MoreVertical,
  CalendarPlus,
  Archive,
  Trash2,
  RefreshCw,
  Loader2,
  Reply,
  Forward,
  Star,
  Ban,
  ChevronDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const channelIcons = {
  whatsapp: MessageCircle,
  email: Mail,
  instagram: Instagram,
  messenger: MessageSquare,
};

const channelColors = {
  whatsapp: 'bg-green-500',
  email: 'bg-blue-500',
  instagram: 'bg-pink-500',
  messenger: 'bg-violet-500',
};

const channelLabels = {
  whatsapp: 'WhatsApp',
  email: 'Email',
  instagram: 'Instagram',
  messenger: 'Messenger',
};

const MessagesPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const params = selectedChannel !== 'all' ? `?channel=${selectedChannel}` : '';
      const response = await axios.get(`${API}/messages${params}`, getAuthHeaders());
      setMessages(response.data);
    } catch (error) {
      console.error('Errore nel caricamento messaggi:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders, selectedChannel]);

  const syncEmails = useCallback(async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/messages/sync-all`, {}, getAuthHeaders());
      await fetchMessages();
    } catch (error) {
      console.error('Errore sincronizzazione:', error);
    } finally {
      setSyncing(false);
    }
  }, [API, getAuthHeaders, fetchMessages]);

  useEffect(() => {
    fetchMessages();
    syncEmails();
  }, [fetchMessages, syncEmails]);

  // Aggiorna la conversazione selezionata quando i messaggi cambiano
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      // Ricostruisci la conversazione con i nuovi messaggi
      const updatedConversation = Object.values(
        [...messages].reduce((acc, msg) => {
          const isOutbound = msg.direction === 'outbound';
          let key;
          
          if (msg.channel === 'email') {
            const emailKey = isOutbound ? msg.recipient_email : msg.sender_email;
            key = `${msg.channel}-${emailKey || msg.sender_name}`;
          } else if (msg.channel === 'whatsapp') {
            const phoneKey = isOutbound ? msg.recipient_phone : msg.sender_phone;
            key = `${msg.channel}-${phoneKey || msg.sender_id}`;
          } else if (msg.channel === 'messenger' || msg.channel === 'instagram') {
            const metaKey = isOutbound ? (msg.recipient_id || msg.sender_id) : msg.sender_id;
            key = `${msg.channel}-${metaKey || msg.sender_name}`;
          } else {
            key = `${msg.channel}-${msg.sender_id || msg.sender_name}`;
          }
          
          if (key === selectedConversation.id) {
            if (!acc[key]) {
              acc[key] = { ...selectedConversation, messages: [] };
            }
            acc[key].messages.push(msg);
          }
          return acc;
        }, {})
      )[0];
      
      if (updatedConversation && updatedConversation.messages.length !== selectedConversation.messages.length) {
        setSelectedConversation(updatedConversation);
      }
    }
  }, [messages, selectedConversation]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/messages/${id}/read`, {}, getAuthHeaders());
      setMessages(messages.map(m => m.id === id ? {...m, is_read: true} : m));
    } catch (error) {
      console.error('Errore nel segnare come letto:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    // Trova l'ultimo messaggio in entrata della conversazione per rispondere
    const inboundMessages = selectedConversation.messages.filter(m => m.direction !== 'outbound');
    const lastInboundMessage = inboundMessages.length > 0 
      ? inboundMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      : selectedConversation.last_message;
    
    if (!lastInboundMessage) {
      console.error('Nessun messaggio a cui rispondere');
      return;
    }
    
    const messageContent = replyText;
    setReplyText(''); // Pulisci subito l'input
    setSending(true);
    
    // Crea un messaggio temporaneo per l'aggiornamento ottimistico
    const tempMessage = {
      id: `temp-${Date.now()}`,
      channel: selectedConversation.channel,
      sender_name: 'You',
      content: messageContent,
      subject: lastInboundMessage.subject ? `Re: ${lastInboundMessage.subject}` : null,
      is_read: true,
      created_at: new Date().toISOString(),
      direction: 'outbound',
      recipient_email: selectedConversation.sender_email,
      recipient_phone: selectedConversation.sender_phone,
      user_id: lastInboundMessage.user_id
    };
    
    // Aggiorna immediatamente la UI
    setSelectedConversation(prev => ({
      ...prev,
      messages: [...prev.messages, tempMessage],
      last_message: tempMessage
    }));
    
    try {
      await axios.post(`${API}/messages/${lastInboundMessage.id}/reply`, 
        { content: messageContent },
        getAuthHeaders()
      );
      // Ricarica i messaggi per sincronizzare con il server
      await fetchMessages();
    } catch (error) {
      console.error('Errore nell\'invio risposta:', error);
      alert('Errore nell\'invio della risposta: ' + (error.response?.data?.detail || error.message));
      // Rimuovi il messaggio temporaneo in caso di errore
      setSelectedConversation(prev => ({
        ...prev,
        messages: prev.messages.filter(m => m.id !== tempMessage.id)
      }));
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    const confirmed = window.confirm('Sei sicuro di voler eliminare questa conversazione?');
    if (!confirmed) return;
    
    try {
      // Elimina tutti i messaggi della conversazione
      const conv = conversations.find(c => c.id === conversationId);
      if (conv && conv.messages) {
        for (const msg of conv.messages) {
          try {
            await axios.delete(`${API}/messages/${msg.id}`, getAuthHeaders());
          } catch (e) {
            console.error(`Errore eliminazione messaggio ${msg.id}:`, e);
          }
        }
      }
      
      // Deseleziona la conversazione se era selezionata
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
      
      // Ricarica i messaggi
      await fetchMessages();
    } catch (error) {
      console.error('Errore eliminazione conversazione:', error);
      alert('Errore durante l\'eliminazione della conversazione');
    }
  };

  const archiveConversation = async (conversationId) => {
    try {
      // Per ora segna come letti tutti i messaggi
      const conv = conversations.find(c => c.id === conversationId);
      if (conv && conv.messages) {
        const unreadMessages = conv.messages.filter(m => !m.is_read);
        for (const msg of unreadMessages) {
          await markAsRead(msg.id);
        }
      }
      // Feedback visivo
      alert('Conversazione archiviata');
    } catch (error) {
      console.error('Errore archiviazione:', error);
    }
  };

  // Ordina i messaggi: prima inbound, poi outbound (per creare le conversazioni con inbound)
  const sortedMessages = [...messages].sort((a, b) => {
    // Inbound first
    if (a.direction !== 'outbound' && b.direction === 'outbound') return -1;
    if (a.direction === 'outbound' && b.direction !== 'outbound') return 1;
    // Then by date
    return new Date(a.created_at) - new Date(b.created_at);
  });

  const groupedMessages = sortedMessages.reduce((acc, msg) => {
    // Per i messaggi in uscita (risposte), usa recipient_email/recipient_phone per raggrupparli
    // con la conversazione originale
    let key;
    const isOutbound = msg.direction === 'outbound';
    
    if (msg.channel === 'email') {
      // Per email in uscita, raggruppa con il destinatario (che è il mittente originale)
      const emailKey = isOutbound ? msg.recipient_email : msg.sender_email;
      key = `${msg.channel}-${emailKey || msg.sender_name}`;
    } else if (msg.channel === 'whatsapp') {
      // Per WhatsApp in uscita, raggruppa con il destinatario
      const phoneKey = isOutbound ? msg.recipient_phone : msg.sender_phone;
      key = `${msg.channel}-${phoneKey || msg.sender_id}`;
    } else if (msg.channel === 'messenger' || msg.channel === 'instagram') {
      // Per Messenger/Instagram in uscita, raggruppa con il destinatario
      const metaKey = isOutbound ? (msg.recipient_id || msg.sender_id) : msg.sender_id;
      key = `${msg.channel}-${metaKey || msg.sender_name}`;
    } else {
      key = `${msg.channel}-${msg.sender_id || msg.sender_name}`;
    }
    
    // Salta i messaggi che non hanno una chiave valida per il raggruppamento
    if (key === `${msg.channel}-undefined` || key === `${msg.channel}-null` || key.endsWith('-')) {
      return acc; // Salta questo messaggio
    }
    
    if (!acc[key]) {
      // Per i messaggi in uscita, non creare una nuova conversazione
      // se non esiste già una conversazione con questo contatto
      if (isOutbound) {
        return acc;
      }
      
      acc[key] = {
        id: key,
        channel: msg.channel,
        sender_name: msg.sender_name,
        sender_id: msg.sender_id,
        sender_email: msg.sender_email,
        sender_phone: msg.sender_phone,
        messages: [],
        last_message: msg,
        unread_count: 0
      };
    }
    
    // Aggiungi il messaggio alla conversazione (sia inbound che outbound)
    acc[key].messages.push(msg);
    if (!msg.is_read && !isOutbound) acc[key].unread_count++;
    if (new Date(msg.created_at) > new Date(acc[key].last_message.created_at)) {
      acc[key].last_message = msg;
    }
    return acc;
  }, {});

  const conversations = Object.values(groupedMessages)
    .filter(c => c.sender_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 c.sender_email?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => new Date(b.last_message.created_at) - new Date(a.last_message.created_at));

  const channelCounts = {
    all: messages.length,
    whatsapp: messages.filter(m => m.channel === 'whatsapp').length,
    email: messages.filter(m => m.channel === 'email').length,
    instagram: messages.filter(m => m.channel === 'instagram').length,
    messenger: messages.filter(m => m.channel === 'messenger').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Messaggi</h1>
          <p className="text-muted-foreground mt-1">Casella unificata per tutti i tuoi canali</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={syncEmails}
          disabled={syncing}
          className="self-start sm:self-auto"
          data-testid="sync-emails-btn"
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Sincronizza Tutti
        </Button>
      </div>

      {/* Contenuto Principale: split desktop, accordion mobile */}
      <div className="md:grid md:grid-cols-12 md:gap-4 md:h-[calc(100vh-200px)]">
        {/* Card lista conversazioni */}
        <Card className="md:col-span-4 md:flex md:flex-col md:overflow-hidden">
          <CardHeader className="pb-3 space-y-3 md:flex-shrink-0">
            {/* Tab Canali */}
            <Tabs value={selectedChannel} onValueChange={setSelectedChannel}>
              <TabsList className="w-full flex justify-start gap-1 h-11 sm:h-9">
                <TabsTrigger value="all" className="text-xs flex-1 min-w-[44px] sm:flex-initial" data-testid="channel-all">
                  Tutti
                </TabsTrigger>
                <TabsTrigger value="whatsapp" className="text-xs flex-1 min-w-[44px] sm:flex-initial" data-testid="channel-whatsapp">
                  <MessageCircle className="w-3.5 h-3.5" />
                </TabsTrigger>
                <TabsTrigger value="email" className="text-xs flex-1 min-w-[44px] sm:flex-initial" data-testid="channel-email">
                  <Mail className="w-3.5 h-3.5" />
                </TabsTrigger>
                <TabsTrigger value="instagram" className="text-xs flex-1 min-w-[44px] sm:flex-initial" data-testid="channel-instagram">
                  <Instagram className="w-3.5 h-3.5" />
                </TabsTrigger>
                <TabsTrigger value="messenger" className="text-xs flex-1 min-w-[44px] sm:flex-initial" data-testid="channel-messenger">
                  <MessageSquare className="w-3.5 h-3.5" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {/* Ricerca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca conversazioni..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-messages"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 md:flex-1 md:overflow-hidden">
            <ScrollArea className="md:h-full [&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]>div]:w-full">
              {conversations.length > 0 ? (
                <div className="divide-y divide-border w-full">
                  {conversations.map((conv, index) => {
                    const Icon = channelIcons[conv.channel] || MessageSquare;
                    const isExpanded = selectedConversation?.id === conv.id;
                    const subtitle = conv.channel === 'email'
                      ? conv.sender_email
                      : conv.channel === 'whatsapp'
                        ? conv.sender_phone
                        : null;

                    return (
                      <div key={conv.id} data-testid={`conversation-${index}`}>
                        {/* Riga conversazione */}
                        <div
                          className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${isExpanded ? 'bg-muted/60' : ''}`}
                          onClick={() => {
                            if (isExpanded) {
                              // On mobile, clicking again collapses. On desktop, keep selected (already shown right).
                              if (window.matchMedia('(max-width: 767px)').matches) {
                                setSelectedConversation(null);
                              }
                            } else {
                              setSelectedConversation(conv);
                              if (conv.unread_count > 0) {
                                conv.messages.filter(m => !m.is_read).forEach(m => markAsRead(m.id));
                              }
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full ${channelColors[conv.channel]} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <h4 className="font-medium truncate">{conv.sender_name}</h4>
                                <span className="text-xs text-muted-foreground flex-shrink-0">
                                  {format(parseISO(conv.last_message.created_at), 'd MMM HH:mm', { locale: it })}
                                </span>
                              </div>
                              {subtitle && (
                                <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
                              )}
                              <p className="text-sm text-muted-foreground truncate mt-0.5">
                                {conv.last_message.direction === 'outbound' && (
                                  <span className="text-primary font-medium">Tu: </span>
                                )}
                                {conv.last_message.subject && conv.last_message.direction !== 'outbound' ? `${conv.last_message.subject}: ` : ''}
                                {conv.last_message.content?.substring(0, 80)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              {conv.unread_count > 0 && !isExpanded && (
                                <Badge className="bg-primary text-primary-foreground">
                                  {conv.unread_count}
                                </Badge>
                              )}
                              {/* Chevron solo su mobile */}
                              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform md:hidden ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                        </div>

                        {/* Thread espanso inline — solo su MOBILE */}
                        {isExpanded && (
                          <div className="border-t bg-muted/20 md:hidden overflow-hidden" data-testid={`thread-${index}`}>
                            <ConversationThread
                              conversation={selectedConversation}
                              channelColors={channelColors}
                              channelIcons={channelIcons}
                              channelLabels={channelLabels}
                              replyText={replyText}
                              setReplyText={setReplyText}
                              sendReply={sendReply}
                              sending={sending}
                              archiveConversation={archiveConversation}
                              deleteConversation={deleteConversation}
                              compact={true}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nessun messaggio trovato</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    I messaggi dai tuoi canali appariranno qui
                  </p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Card thread — solo desktop (md+) */}
        <Card className="hidden md:flex md:col-span-8 md:flex-col md:overflow-hidden">
          {selectedConversation ? (
            <ConversationThread
              conversation={selectedConversation}
              channelColors={channelColors}
              channelIcons={channelIcons}
              channelLabels={channelLabels}
              replyText={replyText}
              setReplyText={setReplyText}
              sendReply={sendReply}
              sending={sending}
              archiveConversation={archiveConversation}
              deleteConversation={deleteConversation}
              compact={false}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold font-outfit mb-1">Seleziona una conversazione</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Scegli una conversazione dalla lista per visualizzare i messaggi e rispondere
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

/**
 * Reusable thread view: header + scrollable messages + reply input.
 * Used both inline (mobile, compact=true) and in right panel (desktop, compact=false).
 */
const ConversationThread = ({
  conversation, channelColors, channelIcons, channelLabels,
  replyText, setReplyText, sendReply, sending,
  archiveConversation, deleteConversation, compact
}) => {
  const Icon = channelIcons[conversation.channel] || MessageSquare;
  const lastInbound = [...(conversation.messages || [])].reverse().find(m => m.direction !== 'outbound' && m.content);
  const messagesEndRef = useRef(null);

  // Auto-scroll al fondo quando arrivano nuovi messaggi o cambia la conversazione
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [conversation.id, conversation.messages.length]);

  return (
    <>
      {/* Header conversazione (più compatto su mobile inline) */}
      {compact ? (
        <div className="flex items-center justify-between p-3 border-b bg-background">
          <p className="text-xs text-muted-foreground truncate">
            {conversation.messages.length} messaggi
            {conversation.sender_email && ` · ${conversation.sender_email}`}
            {conversation.sender_phone && ` · ${conversation.sender_phone}`}
          </p>
          <ConversationActions
            conversation={conversation}
            archiveConversation={archiveConversation}
            deleteConversation={deleteConversation}
          />
        </div>
      ) : (
        <CardHeader className="border-b py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-full ${channelColors[conversation.channel]} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{conversation.sender_name}</h3>
                <p className="text-sm text-muted-foreground truncate">
                  {conversation.sender_email || conversation.sender_phone || channelLabels[conversation.channel]}
                </p>
              </div>
            </div>
            <ConversationActions
              conversation={conversation}
              archiveConversation={archiveConversation}
              deleteConversation={deleteConversation}
            />
          </div>
        </CardHeader>
      )}

      {/* Messaggi - scrollabile */}
      <ScrollArea className={compact ? 'h-[400px]' : 'flex-1'}>
        <div className="p-4 space-y-4">
          {conversation.messages
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            .map((msg) => {
              const isOutbound = msg.direction === 'outbound' || msg.sender_name === 'You' || msg.sender_name === 'Tu';
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 ${
                    isOutbound
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-card border rounded-bl-md'
                  }`}>
                    {msg.subject && (
                      <p className={`text-xs font-semibold mb-1 ${isOutbound ? 'text-primary-foreground/80' : 'text-foreground'}`}>
                        {msg.subject}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 ${
                      isOutbound ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}>
                      <span className="text-xs">
                        {format(parseISO(msg.created_at), 'HH:mm')}
                      </span>
                      {isOutbound && (
                        msg.is_read ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input risposta */}
      <div className="p-3 border-t bg-background flex-shrink-0 space-y-2">
        {lastInbound && (
          <AISuggestReplies kind={conversation.channel === 'comment' ? 'comment' : 'message'} text={lastInbound.content} author={conversation.sender_name} channel={conversation.channel}
            history={(conversation.messages || []).slice(-6).map(m => ({ content: m.content, outbound: m.direction === 'outbound' }))}
            onPick={setReplyText} compact />
        )}
        <div className="flex items-center gap-2 w-full">
          <Input
            placeholder="Scrivi il tuo messaggio..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
            className="flex-1 min-w-0"
            data-testid="reply-input"
          />
          <Button
            onClick={sendReply}
            disabled={sending || !replyText.trim()}
            size="icon"
            className="flex-shrink-0"
            data-testid="send-reply-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );
};

const ConversationActions = ({ conversation, archiveConversation, deleteConversation }) => {
  const navigate = useNavigate();

  const createAppointment = () => {
    // Build URL params with pre-filled customer info
    const params = new URLSearchParams();
    if (conversation.sender_name) params.set('name', conversation.sender_name);
    if (conversation.sender_phone) params.set('phone', conversation.sender_phone);
    if (conversation.sender_email) params.set('email', conversation.sender_email);
    // Try to extract a service name from last messages (chosen via WhatsApp)
    const lastMsgs = (conversation.messages || []).slice(-6);
    const serviceHint = lastMsgs.find((m) => /hai scelto:?\s*([^—\n]+)/i.test(m.content || ''));
    if (serviceHint) {
      const match = serviceHint.content.match(/hai scelto:?\s*\*?([^*—\n]+)\*?/i);
      if (match) params.set('service', match[1].trim().replace(/^\*|\*$/g, ''));
    }
    navigate(`/calendar?newAppointment=1&${params.toString()}`);
  };

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {conversation.sender_phone && (
        <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
          <a href={`tel:${conversation.sender_phone}`}>
            <Phone className="w-4 h-4" />
          </a>
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="conversation-menu-btn">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={createAppointment} data-testid="create-appointment-from-chat">
            <CalendarPlus className="w-4 h-4 mr-2 text-primary" />Crea appuntamento
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => archiveConversation(conversation.id)}>
            <Archive className="w-4 h-4 mr-2" />Archivia
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => deleteConversation(conversation.id)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />Elimina conversazione
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default MessagesPage;
